import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TenantBrandingStore } from './tenant-branding.store';

/**
 * Component for displaying tenant branding elements
 * Shows logo, title, and other tenant-specific branding
 */
@Component({
  selector: 'app-tenant-branding',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="showBranding()" class="tenant-branding">
      <!-- Tenant Logo -->
      <div *ngIf="logoUrl()" class="tenant-logo">
        <img class="logo-image" [alt]="displayName() || 'Tenant Logo'" [src]="logoUrl()" (error)="onLogoError()" />
      </div>

      <!-- Tenant Title and Subtitle -->
      <div *ngIf="tenantTitle() || tenantSubTitle()" class="tenant-content">
        <h1 *ngIf="tenantTitle()" class="tenant-title">
          {{ tenantTitle() }}
        </h1>
        <p *ngIf="tenantSubTitle()" class="tenant-subtitle">
          {{ tenantSubTitle() }}
        </p>
      </div>

      <!-- Loading State -->
      <div *ngIf="loading()" class="tenant-loading">
        <div class="loading-spinner"></div>
        <span>Loading branding...</span>
      </div>

      <!-- Error State -->
      <div *ngIf="hasError()" class="tenant-error">
        <span class="error-message">{{ error() }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      .tenant-branding {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
      }

      .tenant-logo {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .logo-image {
        max-width: 200px;
        max-height: 100px;
        object-fit: contain;
        border-radius: 4px;
      }

      .tenant-content {
        text-align: center;
      }

      .tenant-title {
        font-family: var(--tenant-header-font-family, inherit);
        color: var(--tenant-primary-color, #2563eb);
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0 0 0.5rem 0;
      }

      .tenant-subtitle {
        font-family: var(--tenant-font-family, inherit);
        color: var(--tenant-secondary-color, #64748b);
        font-size: 1rem;
        margin: 0;
      }

      .tenant-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        color: var(--tenant-secondary-color, #64748b);
      }

      .loading-spinner {
        width: 20px;
        height: 20px;
        border: 2px solid var(--tenant-secondary-color, #64748b);
        border-top: 2px solid var(--tenant-primary-color, #2563eb);
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .tenant-error {
        color: #ef4444;
        text-align: center;
      }

      .error-message {
        font-size: 0.875rem;
      }

      /* Responsive design */
      @media (max-width: 768px) {
        .tenant-branding {
          padding: 0.5rem;
        }

        .logo-image {
          max-width: 150px;
          max-height: 75px;
        }

        .tenant-title {
          font-size: 1.25rem;
        }

        .tenant-subtitle {
          font-size: 0.875rem;
        }
      }
    `,
  ],
})
export class TenantBrandingComponent implements OnInit, OnDestroy {
  // Inject store first
  private readonly store = inject(TenantBrandingStore);
  private readonly destroyRef = inject(DestroyRef);
  // State signals
  private readonly _logoError = signal<boolean>(false);
  private readonly _showBranding = signal<boolean>(true);

  // Computed signals
  readonly logoError = this._logoError.asReadonly();
  readonly showBranding = this._showBranding.asReadonly();

  // Store signals
  readonly branding = this.store.branding;
  readonly loading = this.store.loading;
  readonly error = this.store.error;
  readonly hasError = this.store.hasError;
  readonly logoUrl = this.store.logoUrl;
  readonly tenantTitle = this.store.tenantTitle;
  readonly tenantSubTitle = this.store.tenantSubTitle;
  readonly displayName = this.store.displayName;

  constructor() {
    // Watch for branding changes
    effect(() => {
      const branding = this.branding();
      if (branding) {
        console.log('Tenant branding loaded in component:', branding);
      }
    });
  }

  ngOnInit(): void {
    console.log('TenantBrandingComponent initialized');

    // Load tenant branding if not already loaded
    if (!this.store.isReady()) {
      this.store.loadTenantBranding().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  ngOnDestroy(): void {
    console.log('TenantBrandingComponent destroyed');
  }

  /**
   * Handle logo loading error
   */
  onLogoError(): void {
    console.warn('Failed to load tenant logo');
    this._logoError.set(true);
  }

  /**
   * Toggle branding visibility
   */
  toggleBranding(): void {
    this._showBranding.set(!this._showBranding());
  }

  /**
   * Hide branding
   */
  hideBranding(): void {
    this._showBranding.set(false);
  }

  /**
   * Show branding
   */
  showBrandingElement(): void {
    this._showBranding.set(true);
  }
}
